import { Link, useParams } from '@tanstack/react-router'
import type { SVGProps } from 'react'

import type { NavItem } from '@/config/nav'
import { useIsActive } from '@/hooks/useIsActive'
import { cn } from '@/lib/utils'

type NavItemProps = {
  to: string
  label: string
  icon: (props: SVGProps<SVGSVGElement>) => React.ReactNode
  iconActive?: (props: SVGProps<SVGSVGElement>) => React.ReactNode
}

const NavItem = ({ to, icon, iconActive = icon, label }: NavItemProps) => {
  const { eventId } = useParams({ from: '/e/$eventId/_dashboard' })
  // `to` is the unsubstituted route pattern (e.g. "/e/$eventId/agenda");
  // useIsActive compares against the actual pathname, so it needs the
  // interpolated form or every item would always read as inactive.
  const isActive = useIsActive(to.replace('$eventId', eventId))

  const Icon = isActive ? iconActive : icon

  return (
    <Link
      to={to}
      params={{ eventId }}
      className={cn(
        'flex flex-1 flex-col items-center justify-center transition-colors',
        isActive ? 'text-primary' : 'text-muted-foreground',
      )}
    >
      <Icon className="size-6.5" />
      <div className="text-[10px] font-bold uppercase">{label}</div>
    </Link>
  )
}

type DashboardNavigationBarProps = {
  items: NavItem[]
}

export const DashboardNavigationBar = ({
  items,
}: DashboardNavigationBarProps) => {
  return (
    <div className="pb-safe border-border/50 border-t bg-white/80 backdrop-blur-lg">
      <div className="h-bnav flex">
        {items.map((item) => (
          <NavItem
            key={item.to}
            to={item.to}
            label={item.label}
            icon={item.icon}
            iconActive={item.iconActive}
          />
        ))}
      </div>
    </div>
  )
}
